import { z } from 'zod';
import { handleSchema, idSchema } from './common.js';

/** Contraseña: mínimo razonable para un equipo de 5, sin reglas de composición (no aportan). */
export const passwordSchema = z.string().min(8).max(200);
export const emailSchema = z.string().email().max(200);

export const loginSchema = z
  .object({ email: emailSchema, password: z.string().min(1).max(200) })
  .strict();
export type LoginInput = z.infer<typeof loginSchema>;

export const refreshSchema = z.object({ refreshToken: z.string().min(1).max(200) }).strict();
export type RefreshInput = z.infer<typeof refreshSchema>;

export const tokenPairSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  /** ISO 8601; el cliente programa el refresh proactivo con `accessExpiresAt`. */
  accessExpiresAt: z.string(),
});
export type TokenPair = z.infer<typeof tokenPairSchema>;

/**
 * `POST /api/v1/auth/vitto` (TASKS-OPS-U1, apps/api `auth-vitto.controller.ts`): the host's single-use pass for the product session.
 * `.strict()` like the server: any other field is a 422. The answer is the SAME pair as `/auth/login` plus WHO the session acts as.
 */
export const vittoGrantSchema = z.object({ grant: z.string().min(1) }).strict();
export type VittoGrantInput = z.infer<typeof vittoGrantSchema>;

export const vittoIdentitySchema = z.object({
  /** El usuario del PRODUCTO que actúa (no el `sub` de Vitto). */
  userId: z.string(),
  role: z.enum(['admin', 'member', 'viewer']),
  workspaceId: z.string(),
  org: z.string(),
  space: z.string(),
  appInstanceId: z.string(),
  /** La autoridad de escritura: `can_write` del pase Y el scope `app:write`. Un lector es 200 con `canWrite:false`, no un error. */
  canWrite: z.boolean(),
  scope: z.enum(['app:read', 'app:write']),
  /** Fin ABSOLUTO del pase de Vitto (iat + 570 s); informativo: la sesión del producto lleva su propio TTL. */
  grantSessionExpiresAt: z.string(),
});
export type VittoIdentity = z.infer<typeof vittoIdentitySchema>;

export const vittoSessionSchema = tokenPairSchema.extend({ identity: vittoIdentitySchema });
export type VittoSession = z.infer<typeof vittoSessionSchema>;

export const sessionSchema = z.object({
  id: idSchema,
  userAgent: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  /** La sesión desde la que se hizo la petición: la UI no ofrece cerrarla en "las demás". */
  current: z.boolean(),
});
export type SessionView = z.infer<typeof sessionSchema>;

/** El token de arranque va en la cabecera `x-setup-token`, no en el cuerpo: lo valida un guard. */
export const SETUP_TOKEN_HEADER = 'x-setup-token';

export const setupSchema = z
  .object({
    workspace: z.object({ slug: z.string().min(1).max(64), name: z.string().min(1).max(200) }),
    admin: z.object({
      email: emailSchema,
      handle: handleSchema,
      name: z.string().min(1).max(200),
      password: passwordSchema,
    }),
  })
  .strict();
export type SetupInput = z.infer<typeof setupSchema>;
